import { useTheme } from "@shopify/restyle";
import React, { useState } from "react";
import { Platform, TextStyle } from "react-native";

import { HStack, Box, VStack, Text } from "../../primitive";
import {
  InputType,
  PrimativeInputProps,
  Input as PrimitiveInput,
} from "./../../primitive/Input/Input";
import { getInputAutofillStyle } from "./inputAutofillStyle";
import { InputProps, InputSize } from "./types";

const getInputStyles = (
  size: InputSize
): Partial<PrimativeInputProps<"text">> => {
  switch (size) {
    case "large":
      return { paddingVertical: 5 };
    case "small":
      return { paddingVertical: 3 };
    case "tiny":
      return { paddingVertical: 2 };
    case "regular":
    default:
      return { paddingVertical: 4 };
  }
};

const getWrappingHStackStyles = (
  size: InputSize,
  hideleftPadding: boolean
): Partial<PrimativeInputProps<"text">> => {
  switch (size) {
    case "large":
      return { paddingLeft: hideleftPadding ? 0 : 4, paddingRight: 4 };
    case "small":
      return { paddingLeft: hideleftPadding ? 0 : 4, paddingRight: 4 };
    case "tiny":
      return { paddingLeft: hideleftPadding ? 0 : 3, paddingRight: 3 };
    case "regular":
    default:
      return { paddingLeft: hideleftPadding ? 0 : 4, paddingRight: 4 };
  }
};

export const Input = <T extends InputType>({
  testID,
  label,
  name,
  placeholder,
  leftIcon,
  rightIcon,
  subMessage,
  size = "regular",
  hasError,
  isDisabled,
  value,
  onChange,
  type = "text",
  inputRef,
  hideIconWrapper,
  isViewOnly,
  onBlur,
  onFocus,
  subElement,
  selectTextOnFocus,
  autoCapitalize,
  autoComplete,
  inputMode,
  keyboardType,
  maxLength,
  nativeID,
  onKeyPress,
  onSubmitEditing,
  returnKeyType,
  spellCheck,
  textAlign,
  overrideIsFocused,
  overrideIsEditable,
  accessibilityLabel,
}: InputProps<T>) => {
  const theme = useTheme();

  const placeholderColor = theme.colors["text-secondary"];
  const inputBackgroundColor = isDisabled
    ? theme.colors["grayscale-500"]
    : isViewOnly
    ? theme.colors["surface-secondary"]
    : theme.colors["surface-primary"];
  const autofillStyle = getInputAutofillStyle(inputBackgroundColor);

  const textColor = isDisabled
    ? "text-disabled"
    : hasError
    ? "text-error"
    : "text-primary";

  const [isFocused, setIsFocused] = useState(false);

  const isEditable =
    overrideIsEditable !== undefined
      ? overrideIsEditable
      : !isDisabled && !isViewOnly;

  return (
    <Box marginBottom={0} width="100%">
      {label ? (
        <Box pb={2}>
          <Text
            variant="mainTiny"
            color={hasError ? "status-error-500" : "grayscale-800"}
          >
            {label}
          </Text>
        </Box>
      ) : null}
      <HStack
        gap={2}
        alignItems={"center"}
        borderRadius={"2"}
        borderColor={
          hasError
            ? "outline-status-error"
            : isFocused || overrideIsFocused
            ? "grayscale-1000"
            : "grayscale-500"
        }
        borderWidth={1.5}
        backgroundColor={
          isDisabled
            ? "grayscale-500"
            : isViewOnly
            ? "surface-secondary"
            : "surface-primary"
        }
        {...getWrappingHStackStyles(size, hideIconWrapper ?? false)}
      >
        {leftIcon ? (
          hideIconWrapper ? (
            leftIcon
          ) : (
            <Box height={16} width={16}>
              {React.cloneElement(leftIcon, {
                color: textColor,
              })}
            </Box>
          )
        ) : null}
        <Box flex={1}>
          {/* Remount on web so Safari redraws autofilled passwords without disrupting native focus. */}
          <PrimitiveInput
            key={
              Platform.OS === "web"
                ? type === "password"
                  ? "secure"
                  : "unsecure"
                : undefined
            }
            {...getInputStyles(size)}
            width="100%"
            style={{ ...autofillStyle } as TextStyle}
            onFocus={(e) => {
              if (isEditable) {
                setIsFocused(true);
                onFocus?.(e);
              }
            }}
            onBlur={(e) => {
              setIsFocused(false);
              onBlur?.(e);
            }}
            type={type}
            selectTextOnFocus={selectTextOnFocus}
            autoCapitalize={autoCapitalize}
            autoComplete={autoComplete}
            inputMode={inputMode}
            keyboardType={keyboardType}
            maxLength={maxLength}
            nativeID={nativeID}
            onKeyPress={onKeyPress}
            onSubmitEditing={onSubmitEditing}
            returnKeyType={returnKeyType}
            spellCheck={spellCheck}
            inputRef={inputRef}
            name={name}
            value={value}
            placeholder={placeholder}
            editable={isEditable}
            aria-disabled={isDisabled}
            focusable={isEditable}
            testID={testID ?? "text-input"}
            color={textColor}
            placeholderTextColor={placeholderColor}
            // iOS shifts single-line TextInput glyphs up and clips them when lineHeight is set
            lineHeight={Platform.OS === "ios" ? undefined : 16}
            fontSize={16}
            letterSpacing={16 * -0.02}
            fontFamily={
              Platform.OS === "web"
                ? "Inter_600SemiBold, Inter"
                : "Inter_600SemiBold"
            }
            secureTextEntry={type === "password"}
            onChange={onChange}
            textAlign={textAlign}
            accessibilityLabel={accessibilityLabel}
          />
        </Box>
        {subElement ? (
          <VStack
            testID="input-sub-element"
            alignSelf="stretch"
            justifyContent={"center"}
            alignItems={"center"}
            flexShrink={1}
          >
            {subElement}
          </VStack>
        ) : null}

        {rightIcon ? (
          <Box height={16} width={16}>
            {React.cloneElement(rightIcon, {
              color: textColor,
            })}
          </Box>
        ) : null}
      </HStack>

      {subMessage ? (
        <Text
          mt={2}
          variant="mainTiny"
          color={hasError ? "status-error-500" : "grayscale-800"}
        >
          {subMessage}
        </Text>
      ) : null}
    </Box>
  );
};
